import { uniqWith } from 'es-toolkit';
import React, { useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Theme,
  Typography,
} from '@mui/material';

import { DEFAULT_ORGANIZATION_SETTINGS, DistanceFormat } from '@paragon/contract-library';
import { ENGINEERING_ORGANIZATION_REGION_DEFINITIONS, isLoggedInUserRoot } from '@paragon/login-tools';
import { DistanceField } from '@paragon/material';
import { enqueueError } from '@paragon/notification-tools';
import { createScrollbarStyling } from '@paragon/themes';
import { notNullish } from '@paragon/utilities';

import {
  addCustomerToOrganization,
  removeCustomerToOrganization,
  updateOrganization,
  upsertOrganizationSettings,
} from '../../actions/organizationActions';
import { getOrganizationsSorted } from '../../selectors/organizations';
import { useAppDispatch, useAppSelector } from '../hooks';

const buttonSx = {
  my: 1,
  py: 1,
  width: '100%',
  bgcolor: 'backContrast.main',
  '&:hover': {
    bgcolor: 'primary.dark',
    color: '#fff',
  },
} as const;

const listSx = (theme: Theme) =>
  ({
    bgcolor: 'backContrast.light',
    maxHeight: 150,
    mb: 3,
    overflowX: 'hidden',
    overflowY: 'auto',
    ...createScrollbarStyling(theme),
  }) as const;

const validate = {
  customer(value: string) {
    if (value.trim() === '') {
      return 'Please select a Customer';
    }

    return null;
  },

  location(value: string) {
    if (value.trim() === '') {
      return 'Please enter an Organization Location';
    }

    return null;
  },
};

export default function EditOrganization() {
  const dispatch = useAppDispatch();
  const organizations = useAppSelector(getOrganizationsSorted);
  const isRoot = useAppSelector(isLoggedInUserRoot);

  const [showLocationError, setShowLocationError] = useState<boolean>(false);
  const [showCustomerError, setShowCustomerError] = useState<boolean>(false);
  const [orgIndex, setOrgIndex] = useState<number>(0);
  const [custValue, setCustValue] = useState<string>('');
  const [locationInputText, setLocationInputText] = useState<string>('');

  const organization = organizations[orgIndex]?.name ?? '';
  const organizationSettings = useAppSelector((state) => state.organizationSettings[organization]) ?? {
    ...DEFAULT_ORGANIZATION_SETTINGS,
    organization,
  };

  const onDeleteLocation = (index: number) => {
    const updatedLocations = organizations[orgIndex].locations ? organizations[orgIndex].locations : [];
    if (index < 0 || index > updatedLocations.length - 1) return;
    dispatch(
      updateOrganization({
        ...organizations[orgIndex],
        locations: updatedLocations.toSpliced(index, 1),
      }),
    );
  };

  const onDeleteCustomer = (index: number) => {
    const updatedCustomers = organizations[orgIndex].customers;
    if (index < 0 || index > updatedCustomers.length - 1) return;
    const customerToRemoveName = updatedCustomers.find((organization, i) => i === index)!;
    dispatch(removeCustomerToOrganization(organizations[orgIndex], customerToRemoveName));
  };

  const onEditOrg = () => {
    if (validateLocation()) {
      let updatedLocations = organizations[orgIndex].locations ? [...organizations[orgIndex].locations] : [];
      updatedLocations.push(locationInputText);
      updatedLocations = uniqWith(updatedLocations, (a, b) => {
        const alpha = a.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/gi, '').toLowerCase();
        const beta = b.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/gi, '').toLowerCase();
        return alpha === beta;
      });
      dispatch(
        updateOrganization({
          ...organizations[orgIndex],
          locations: updatedLocations,
        }),
      );
      setLocationInputText('');
      setShowLocationError(false);
    }
  };

  const updateOrganizationDropDown = (event: SelectChangeEvent<number>) => {
    setOrgIndex(event.target.value as number);
  };

  const updateCustomerDropDown = (event: SelectChangeEvent<string>) => {
    setCustValue(event.target.value);
  };

  const addCustomerToActiveOrganization = () => {
    if (validateCustomer()) {
      dispatch(addCustomerToOrganization(organizations[orgIndex], custValue));
      setCustValue('');
      setShowCustomerError(false);
    }
  };

  const locationChange: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    setLocationInputText(event.target.value);
  };

  function validateLocation() {
    const errors = [validate.location(locationInputText)].filter(notNullish);

    if (errors.length > 0) {
      setShowLocationError(true);
      errors.forEach((error) => {
        dispatch(enqueueError(error));
      });
      return false;
    }

    return true;
  }

  function validateCustomer() {
    const errors = [validate.customer(custValue)].filter(notNullish);

    if (errors.length > 0) {
      setShowCustomerError(true);
      errors.forEach((error) => {
        dispatch(enqueueError(error));
      });
      return false;
    }

    return true;
  }

  function renderLocationList() {
    const currentOrganization = organizations[orgIndex];
    if (!currentOrganization || currentOrganization.locations.length === 0) {
      return (
        <List sx={listSx}>
          <ListItem>
            <ListItemText primary="None" />
          </ListItem>
        </List>
      );
    }

    const locationTexts = currentOrganization.locations.map((loc, count) => (
      <ListItem key={`locItem${count}`}>
        <ListItemText primary={loc} />
        <ListItemSecondaryAction>
          <IconButton
            aria-label="Remove"
            onClick={() => {
              onDeleteLocation(count);
            }}
          >
            <ClearIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    ));
    return <List sx={listSx}>{locationTexts}</List>;
  }

  function renderCustomersList() {
    const currentOrganization = organizations[orgIndex];
    if (!currentOrganization) {
      return <CircularProgress />;
    }

    const customerListItems = currentOrganization.customers.map((customer, index) => (
      <ListItem key={`customerItem${index}`}>
        <ListItemText primary={customer} />
        <ListItemSecondaryAction>
          <IconButton
            aria-label="Remove"
            onClick={() => {
              onDeleteCustomer(index);
            }}
          >
            <ClearIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    ));
    return <List sx={listSx}>{customerListItems}</List>;
  }

  const organizationNames = organizations.map((o) => o.name);
  const currentOrganization = organizations[orgIndex];

  const nonCustomerOrganizations = new Set();
  if (currentOrganization) {
    nonCustomerOrganizations.add(currentOrganization.name);
    currentOrganization.customers.forEach((customer) => nonCustomerOrganizations.add(customer));
  }

  const customerOptions = organizationNames.filter((o) => !nonCustomerOrganizations.has(o));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '35%', p: 1 }}>
      <Typography variant="h6">Edit Organization</Typography>
      <Divider sx={{ mb: 4 }} />
      <Paper sx={{ p: 2, pt: 0, bgcolor: 'backContrast.main' }} elevation={1}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2, mt: 1 }}>
            <FormControl variant="standard" sx={{ m: 1, ml: 0, flex: 1 }}>
              <Select value={orgIndex} disabled={!isRoot} onChange={updateOrganizationDropDown}>
                {organizationNames.map((name, index) => (
                  <MenuItem key={index} value={index}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Typography sx={{ mb: 1 }}>Locations</Typography>
          <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>{renderLocationList()}</Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2, mt: 1 }}>
            <FormControl sx={{ flex: 1 }}>
              <TextField
                variant="standard"
                required
                error={showLocationError && validate.location(locationInputText) != null}
                id="add-location"
                label="Enter New Location"
                sx={{ mr: 1, width: '100%' }}
                value={locationInputText}
                onChange={locationChange}
              />
            </FormControl>
          </Box>
          <Button sx={buttonSx} variant="outlined" onClick={onEditOrg}>
            Add New Location
          </Button>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ mb: 1 }}>Customers</Typography>
          {renderCustomersList()}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2, mt: 1 }}>
            <FormControl variant="standard" sx={{ m: 1, ml: 0, flex: 1 }}>
              <InputLabel htmlFor="customers">Select Customer</InputLabel>
              <Select
                error={showCustomerError && validate.customer(custValue) != null}
                value={custValue}
                onChange={updateCustomerDropDown}
                inputProps={{
                  name: 'customers',
                  id: 'customers',
                }}
                label="Select Customer"
              >
                {customerOptions.map((option, index) => (
                  <MenuItem key={index} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button variant="outlined" sx={buttonSx} onClick={addCustomerToActiveOrganization}>
            Add Customer
          </Button>
        </Box>
        <Divider sx={{ mb: 3 }} />
        {isRoot && (
          <>
            <Autocomplete
              multiple
              options={organizationNames.filter((organizationName) => organizationName !== currentOrganization.name)}
              renderInput={(params) => <TextField {...params} variant="outlined" label="Visible Organization(s)" />}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {option}
                </li>
              )}
              value={currentOrganization ? [...currentOrganization.visibleOrganizations] : []}
              onChange={(event, visibleOrganizations) => {
                dispatch(
                  updateOrganization({
                    ...currentOrganization,
                    visibleOrganizations,
                  }),
                );
              }}
            />

            {currentOrganization &&
              currentOrganization.visibleOrganizations.some(
                (organization) => !(organization in ENGINEERING_ORGANIZATION_REGION_DEFINITIONS),
              ) && (
                <Alert severity="warning" variant="outlined" sx={{ marginTop: 1, width: 0, minWidth: '100%' }}>
                  Some visible organizations are not configured with region metadata and will not be avaliable to users
                  requesting seals in the design client. Talk to David or poke around in
                  `sealingOrganizationDefinitions.ts` to resolve this issue.
                </Alert>
              )}
          </>
        )}
        <Divider sx={{ mb: 3 }} />
        <Typography sx={{ mb: 1 }}>Organization Settings</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, mb: 2, mt: 1, flex: 1 }}>
          <FormControl sx={{ m: 2, flexBasis: { md: '100%' } }}>
            <InputLabel htmlFor="distanceFormat">Distance Format</InputLabel>
            <Select
              value={organizationSettings.useDistanceFormat}
              onChange={(event) => {
                if (organization === '') return;

                dispatch(
                  upsertOrganizationSettings({
                    ...organizationSettings,
                    useDistanceFormat: event.target.value as DistanceFormat,
                  }),
                );
              }}
              inputProps={{
                name: 'distanceFormat',
                id: 'distanceFormat',
              }}
              label="Distance Format"
            >
              <MenuItem value="FeetInchesSixteenths">Feet-Inches-Sixteenths</MenuItem>
              <MenuItem value="Architectural">Architectural</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ m: 2, flexBasis: { md: '100%' } }}>
            <InputLabel htmlFor="squareCutAngle">Square Cut Angle</InputLabel>
            <Select
              value={organizationSettings.squareCutAngleIsZero ? '0' : '90'}
              onChange={(event) => {
                if (organization === '') return;

                dispatch(
                  upsertOrganizationSettings({
                    ...organizationSettings,
                    squareCutAngleIsZero: event.target.value === '0',
                  }),
                );
              }}
              inputProps={{
                name: 'squareCutAngle',
                id: 'squareCutAngle',
              }}
              label="Square Cut Angle"
            >
              <MenuItem value="90">90 degrees</MenuItem>
              <MenuItem value="0">0 degrees</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ m: 2, flexBasis: { md: '100%' } }}>
            <InputLabel htmlFor="shopDrawingOrientation">Shop Drawing Orientation</InputLabel>
            <Select
              value={organizationSettings.portraitOrientationShopDrawings ? 'Portrait' : 'Landscape'}
              onChange={(event) => {
                if (organization === '') return;

                dispatch(
                  upsertOrganizationSettings({
                    ...organizationSettings,
                    portraitOrientationShopDrawings: event.target.value === 'Portrait',
                  }),
                );
              }}
              inputProps={{
                name: 'shopDrawingOrientation',
                id: 'shopDrawingOrientation',
              }}
              label="Shop Drawing Orientation"
            >
              <MenuItem value="Landscape">Landscape</MenuItem>
              <MenuItem value="Portrait">Portrait</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ m: 2, flexBasis: { md: '100%' } }}>
            <DistanceField
              label="Scarf Cut Threshold"
              value={organizationSettings.scarfCutThreshold}
              min={1 / 16}
              onSubmit={async (value) => {
                if (organization === '') return;

                await dispatch(upsertOrganizationSettings({ ...organizationSettings, scarfCutThreshold: value }));
              }}
              size="medium"
              sx={{ m: 0 }}
            />
          </FormControl>
        </Box>
        <FormControlLabel
          sx={{ mx: 2 }}
          label="Append the date and time to download file names"
          control={
            <Checkbox
              checked={organizationSettings.appendDateAndTimeToDownloadFileNames}
              onChange={(event) => {
                if (organization === '') return;

                dispatch(
                  upsertOrganizationSettings({
                    ...organizationSettings,
                    appendDateAndTimeToDownloadFileNames: event.target.checked,
                  }),
                );
              }}
            />
          }
        />
      </Paper>
    </Box>
  );
}
